"use client";
import React, { useRef, useState } from "react";
import styles from "./Listbox.module.scss";
import Link from "next/link";
import { FocusTrap } from "@aleksundshantu/aunds-utils-react/accessibility";
import { ClickOutside } from "@aleksundshantu/aunds-utils-react/accessibility";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { CustomSVG } from "../_primitives/CustomSVG/CustomSVG.client";
import { DotChevron } from "../DotChevron/DotChevron";

interface ListProps {
  items: { label: string; value: string }[];
  selected: string;
  setSelected: (value: string) => void;
  disableDropdown?: boolean;
  availableTranslations: { lang: string; path: string }[];
  alwaysOpenUntil?: "navbar-breakpoint";
  closeOnSelection?: boolean;
  selectedIconKey?: string;
  openOnHover?: boolean;
}

export const List = ({
  items,
  selected: _selected,
  setSelected: _setSelected,
  disableDropdown,
  availableTranslations,
  alwaysOpenUntil,
  closeOnSelection,
  selectedIconKey,
  openOnHover,
}: ListProps) => {
  //state that is only used internally if no external state is provided
  const [internalSelected, setInternalSelected] = useState<string | undefined>(
    undefined,
  );
  const selected = _selected ?? internalSelected;
  const setSelected = _setSelected ?? setInternalSelected;

  const [open, setOpen] = useState(false);
  // hover-opened menus must not steal focus, so the focus trap stays off for them
  const [openedByHover, setOpenedByHover] = useState(false);
  const isHoverEnabled = openOnHover && !disableDropdown;

  const ref = useRef<HTMLUListElement>(null);
  const button = useRef<HTMLButtonElement>(null);

  const { label } = useGlobalContext();

  // mouse only — touch taps fire pointerenter too and would fight the click toggle
  const onPointerEnter = (e: React.PointerEvent) => {
    if (!isHoverEnabled || e.pointerType !== "mouse") return;
    setOpenedByHover(true);
    setOpen(true);
  };

  const onPointerLeave = (e: React.PointerEvent) => {
    if (!isHoverEnabled || e.pointerType !== "mouse" || !openedByHover) return;
    setOpenedByHover(false);
    setOpen(false);
  };

  // ARIA APG menu pattern: arrows move between items (wrapping), Home/End jump to the ends
  const onMenuKeyDown = (e: React.KeyboardEvent<HTMLUListElement>) => {
    const menuItems = Array.from(
      e.currentTarget.querySelectorAll<HTMLElement>('[role="menuitem"]'),
    );
    if (menuItems.length === 0) return;

    const currentIndex = menuItems.findIndex(
      (item) => item === document.activeElement,
    );
    const lastIndex = menuItems.length - 1;
    const nextIndexByKey: Partial<Record<string, number>> = {
      ArrowDown: currentIndex >= lastIndex ? 0 : currentIndex + 1,
      ArrowUp: currentIndex <= 0 ? lastIndex : currentIndex - 1,
      Home: 0,
      End: lastIndex,
    };
    const nextIndex = nextIndexByKey[e.key];
    if (nextIndex === undefined) return;

    e.preventDefault(); // stop the page from scrolling
    menuItems[nextIndex].focus();
  };

  return (
    <div
      data-is-open={open}
      className={`${styles.listbox} dropdown ${open ? "is-active" : ""} ${alwaysOpenUntil ? styles[`always-open-until-${alwaysOpenUntil}`] : ""}`}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
    >
      <div className={`dropdown-trigger ${disableDropdown ? "disabled" : ""}`}>
        <button
          className="button is-flex is-align-items-center is-gap-1"
          aria-haspopup="true"
          aria-controls="dropdown-menu"
          aria-expanded={open}
          onClick={() => {
            // already open from hover — a click must not close it under the cursor
            if (openedByHover && open) return;
            setOpenedByHover(false);
            setOpen(!open);
          }}
          // Enter/Space already fire a native click on <button> — handling them here would toggle twice
          onKeyUp={(e) => {
            if (e.key === "Escape") {
              setOpen(false);
            }
          }}
          ref={button}
        >
          <span className="navbar-link selected-lang">
            {selected
              ? items.find((i) => i.value === selected)?.label
              : "Auswählen..."}
          </span>
          {!disableDropdown && <DotChevron isOpen={open} />}
        </button>
      </div>
      {!disableDropdown && (
        <div className="dropdown-menu" id="dropdown-menu">
          <ClickOutside
            elementToExclude={button}
            onClickOutside={() => setOpen(false)}
          >
            {/* eslint-disable-next-line jsx-a11y/no-autofocus -- ARIA APG: focus must move into dropdown on open */}
            <FocusTrap dep={open} disabled={!open || openedByHover} autoFocus>
              <ul
                role="menu"
                className="dropdown-content m-0"
                onKeyDown={onMenuKeyDown}
                onKeyUp={(e) => {
                  if (e.key === "Escape") {
                    setOpen(false);
                    ref.current?.focus();
                  }
                }}
                aria-label={label("global", "aria_label", "language_toggle", {
                  en: "Language menu",
                  de: "Sprachmenü",
                })}
              >
                {items.map((item, index) => {
                  const translatedPath = availableTranslations.find(
                    (x) => x.lang === item.value,
                  );
                  const isSelected = selected === item.value;

                  return (
                    <li className="dropdown-item m-0" key={index} role="none">
                      {
                        // eslint-disable-next-line react/forbid-elements
                        <Link
                          role="menuitem"
                          href={translatedPath?.path ?? ""}
                          locale={item.value}
                          prefetch={false}
                          aria-label={
                            label(
                              "global",
                              "aria_label",
                              "language_toggle_option_" + item.value,
                              "",
                            ) || undefined
                          }
                          aria-current={isSelected ? "true" : undefined}
                          className={` navbar-link ${isSelected ? "is-active" : ""}`}
                          onClick={() => {
                            if (closeOnSelection) setOpen(false);
                            setSelected(item.value);
                          }}
                          lang={item.value}
                          hrefLang={item.value}
                        >
                          <span className="dropdown-item-label">
                            {item.label}
                          </span>
                          {selectedIconKey && (
                            <span
                              className={`${styles["selected-icon-slot"]} ${isSelected ? styles["is-visible"] : ""}`}
                            >
                              <CustomSVG
                                iconKey={selectedIconKey}
                                wrapper={"span"}
                                width={15}
                                height={12}
                                aria-hidden="true"
                                className="is-flex"
                              />
                            </span>
                          )}
                        </Link>
                      }
                    </li>
                  );
                })}
              </ul>
            </FocusTrap>
          </ClickOutside>
        </div>
      )}
    </div>
  );
};
/* jscpd:ignore-start */
export type ListPropsV2 = {
  buttonId?: string;
  items: { label: string; value: string }[];
  selected?: string;
  setSelected: (value: string) => void;
  disableDropdown?: boolean;
  availableTranslations?: { lang: string; path: string }[];
  alwaysOpenUntil?: "navbar-breakpoint";
  className?: string;
  placeholder?: string;
  "aria-describedby"?: string;
};

export const ListV2 = ({
  items,
  selected: _selected,
  setSelected: _setSelected,
  disableDropdown,
  availableTranslations,
  alwaysOpenUntil,
  buttonId,
  className = "",
  placeholder = "Auswählen...",
  "aria-describedby": ariaDescribedby,
}: ListPropsV2) => {
  //state that is only used internally if no external state is provided
  const [internalSelected, setInternalSelected] = useState<string | undefined>(
    undefined,
  );
  const selected = _selected ?? internalSelected;
  const setSelected = _setSelected ?? setInternalSelected;

  const [open, setOpen] = useState(false);

  const ref = useRef<HTMLUListElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const { getIcon } = useGlobalContext();

  return (
    <div
      data-is-open={open}
      className={`${styles["listbox"]} ${className} dropdown ${open ? "is-active" : ""} ${
        alwaysOpenUntil ? styles[`always-open-until-${alwaysOpenUntil}`] : ""
      }`}
    >
      <div
        className={`dropdown-trigger w-100 ${
          disableDropdown ? "disabled" : ""
        }`}
      >
        <button
          type={"button"} // important! otherwise forms with listbox inside will be submitted
          id={buttonId}
          className="button is-flex is-align-items-center is-justify-content-space-between is-gap-1 has-text-left w-100"
          aria-haspopup="true"
          aria-controls="dropdown-menu"
          aria-describedby={ariaDescribedby}
          onClick={() => {
            setOpen(!open);
          }}
          // Enter/Space already fire a native click on <button> — handling them here would toggle twice
          onKeyUp={(e) => {
            if (e.key === "Escape") {
              setOpen(false);
            }
          }}
          ref={button}
        >
          <span
            className={
              availableTranslations
                ? "navbar-link selected-lang"
                : `placeholder ${selected ? "is-selected" : ""}`
            }
          >
            {selected
              ? items.find((i) => i.value === selected)?.label
              : placeholder}
          </span>
          {!disableDropdown && (
            <div className={styles["arrow-wrapper"]}>
              <CustomSVG
                src={getIcon("chevron-down")}
                wrapper={"span"}
                width={12}
                height={12}
                alt="chevron-down"
                className="is-flex"
              />
            </div>
          )}
        </button>
      </div>
      {!disableDropdown && (
        <div
          className="dropdown-menu w-100"
          id="dropdown-menu"
          data-lenis-prevent
        >
          <ClickOutside
            elementToExclude={button}
            onClickOutside={() => setOpen(false)}
          >
            {/* eslint-disable-next-line jsx-a11y/no-autofocus -- ARIA APG: focus must move into listbox on open */}
            <FocusTrap dep={open} disabled={!open} autoFocus>
              <ul
                role="listbox"
                className="dropdown-content m-0"
                onKeyUp={(e) => {
                  if (e.key === "Escape") {
                    setOpen(false);
                    ref.current?.focus();
                  }
                }}
              >
                {items.map((item, index) => {
                  const translatedPath = availableTranslations?.find(
                    (x) => x.lang === item.value,
                  );

                  if (translatedPath) {
                    return (
                      <li className="dropdown-item m-0" key={index}>
                        {
                          // eslint-disable-next-line react/forbid-elements
                          <Link
                            role="menuitem"
                            href={translatedPath?.path ?? ""}
                            locale={item.value}
                            prefetch={false}
                            className={` navbar-link ${
                              selected === item.value ? "is-active" : ""
                            }`}
                            onClick={() => setSelected(item.value)}
                            lang={item.value}
                            hrefLang={item.value}
                          >
                            {item.label}
                          </Link>
                        }
                      </li>
                    );
                  }

                  return (
                    <li
                      className="dropdown-item m-0"
                      key={index}
                      role="option"
                      aria-selected={selected === item.value}
                      onKeyDown={(
                        event: React.KeyboardEvent<HTMLLIElement>,
                      ) => {
                        if (event.key === "Enter") {
                          setOpen(false);
                          setSelected(item.value);
                        }
                      }}
                      onClick={() => {
                        setOpen(false);
                        setSelected(item.value);
                      }}
                    >
                      {item.label}
                    </li>
                  );
                })}
              </ul>
            </FocusTrap>
          </ClickOutside>
        </div>
      )}
    </div>
  );
};
/* jscpd:ignore-end */
